import React, { useState } from "react";
import { changePassword, setToken } from "../api";

/**
 * Full-screen blocker for accounts created by an institute admin with a
 * temporary password (profile.force_password_change). The backend also blocks
 * all HasAppAccess endpoints until the password is changed, so this screen is
 * UX, not the security boundary.
 */
function ForcePasswordChange({ onChanged, onLogout }) {
  const [current, setCurrent] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPw.length < 8) {
      setError("Your new password must be at least 8 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      setError("Passwords do not match.");
      return;
    }
    if (newPw === current) {
      setError("Choose a different password from your temporary one.");
      return;
    }
    setLoading(true);
    try {
      const data = await changePassword(current, newPw);
      if (data.token) setToken(data.token);
      onChanged();
    } catch (err) {
      setError(err.message || "Could not change your password.");
      setLoading(false);
    }
  };

  return (
    <div className="dark-scope relative min-h-screen flex items-center justify-center px-4 py-10 overflow-hidden" style={{ background: "var(--ink)" }}>
      <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-ink-100/50 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 -left-24 w-96 h-96 rounded-full bg-gold-100/40 blur-3xl" />
      <div className="relative z-10 w-full max-w-sm animate-rise">
        <div className="text-center mb-7">
          <span className="text-6xl font-arabic-display text-ink-600 leading-none" dir="rtl">مراجعة</span>
          <div className="rule-ornament w-40 mx-auto mt-3"><span className="text-gold-400">✦</span></div>
        </div>
        <div className="bg-parchment-50 rounded-2xl shadow-soft-lg border border-parchment-300/70 p-8">
          <h2 className="text-2xl font-display font-semibold text-ink-800 mb-2">Choose your password</h2>
          <p className="text-sm text-slate-500 mb-6">
            Your account was set up with a temporary password. Pick your own to start learning.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1.5">
                Temporary password
              </label>
              <input
                type="password" required autoFocus value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-parchment-300 bg-white/80 text-base text-ink-800 focus:outline-none focus:ring-2 focus:ring-ink-400 focus:border-ink-400 transition"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1.5">
                New password
              </label>
              <input
                type="password" required minLength={8} value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-parchment-300 bg-white/80 text-base text-ink-800 focus:outline-none focus:ring-2 focus:ring-ink-400 focus:border-ink-400 transition"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1.5">
                Confirm new password
              </label>
              <input
                type="password" required value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-parchment-300 bg-white/80 text-base text-ink-800 focus:outline-none focus:ring-2 focus:ring-ink-400 focus:border-ink-400 transition"
              />
            </div>

            {error && <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</p>}

            <button
              type="submit" disabled={loading}
              className="w-full py-2.5 rounded-xl bg-ink-600 text-parchment-50 font-semibold text-sm hover:bg-ink-700 disabled:opacity-50 shadow-soft transition-colors"
            >
              {loading ? "Saving…" : "Set my password"}
            </button>
          </form>

          <button
            onClick={onLogout}
            className="mt-3 w-full py-2 text-slate-400 hover:text-slate-600 text-sm transition-colors"
          >
            Log out
          </button>
        </div>
      </div>
    </div>
  );
}

export default ForcePasswordChange;
